import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { trFold } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ArrowLeft, Plus, Trash2, Users, Search, Settings, FolderOpen, Check, X, Download, Upload, FileSpreadsheet } from "lucide-react";

const KIND_CLS = {
  SEM: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  CM: "bg-blue-500/10 text-blue-700 border-blue-500/30",
};

const CONTACT_ZONES = ["Blue Zone", "Green Zone", "Ofisler"];
const SEM_DISABLED_ZONES = ["Blue Zone"];
const zoneOptsFor = (kind) => (kind === "SEM" ? CONTACT_ZONES.filter((z) => !SEM_DISABLED_ZONES.includes(z)) : CONTACT_ZONES);
const kindOptsFor = (zone) => (SEM_DISABLED_ZONES.includes(zone) ? ["CM"] : ["SEM", "CM"]);
const ZONE_CLS = {
  "Blue Zone": "bg-blue-500/10 text-blue-700 border-blue-500/30",
  "Green Zone": "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  "Ofisler": "bg-amber-500/10 text-amber-700 border-amber-500/30",
};

const CellInput = ({ value, onSave, placeholder, testid }) => {
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val.trim() !== (value || "")) onSave(val.trim()); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      placeholder={placeholder}
      data-testid={testid}
      className="rounded-none h-8 text-sm bg-transparent border-transparent hover:border-slate-200 focus:bg-white focus:border-slate-300 px-2 placeholder:text-slate-300"
    />
  );
};

const AssignProjectsDialog = ({ contact, projects, base, onClose, onSaved, idp }) => {
  const [sel, setSel] = useState(() => new Set(contact.project_ids || []));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const term = trFold(q.trim());
  const czone = contact.zone || "Green Zone";
  const list = [...projects]
    .filter((p) => p.zone === czone)
    .filter((p) => !term || [p.name, p.stand_no, p.zone].some((v) => trFold(v || "").includes(term)))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr", { sensitivity: "base" }));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`${base}/contacts/${contact.id}/assign`, { project_ids: [...sel] });
      onSaved([...sel]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg" data-testid={`${idp}-assign-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Badge variant="outline" className={`rounded-none text-[10px] ${KIND_CLS[contact.kind] || ""}`}>{contact.kind}</Badge>
            <Badge variant="outline" className={`rounded-none text-[10px] ${ZONE_CLS[contact.zone || "Green Zone"] || ""}`}>{contact.zone || "Green Zone"}</Badge>
            {contact.name} — Proje Ata
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, stand ara..." data-testid={`${idp}-assign-search`} className="rounded-none h-9 pl-9 bg-[#F5F7FA] border-slate-200" />
        </div>
        <div className="border border-slate-200 max-h-[48vh] overflow-y-auto divide-y divide-slate-100" data-testid={`${idp}-assign-list`}>
          {list.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-slate-400">{czone} bölgesinde proje bulunamadı</div>
          ) : (
            list.map((p) => (
              <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`${idp}-assign-item-${p.id}`}>
                <input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} data-testid={`${idp}-assign-check-${p.id}`} className="accent-slate-900 w-4 h-4 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold truncate">{p.name}</span>
                  <span className="block text-[11px] text-slate-400">{p.zone}{p.stand_no ? ` · Stand ${p.stand_no}` : ""}</span>
                </span>
              </label>
            ))
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500" data-testid={`${idp}-assign-count`}>{sel.size} proje seçili</span>
          <Button onClick={save} disabled={busy} data-testid={`${idp}-assign-save`} className="rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ContactsManager = ({ base = "", projectsEndpoint = "/projects", idp = "contacts" }) => {
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.get(`${base}/contacts`).then((r) => setContacts(r.data || [])).catch(() => {}).finally(() => setLoaded(true));
  }, [base]);

  const addDraft = () => setDrafts((ds) => [...ds, { key: Date.now() + Math.random(), kind: "SEM", zone: "Green Zone", name: "", phone: "", email: "" }]);
  const setDraft = (key, d) => setDrafts((ds) => ds.map((x) => (x.key === key ? d : x)));

  const saveDraft = async (d) => {
    if (!d.name.trim()) return toast.error("Ad soyad girin");
    if (d.email.trim() && !d.email.includes("@")) return toast.error("Geçerli bir e-posta girin");
    try {
      const { data } = await api.post(`${base}/contacts`, { kind: d.kind, zone: d.zone, name: d.name.trim(), phone: d.phone.trim() || null, email: d.email.trim() || null });
      setContacts((cs) => [...cs, data]);
      setDrafts((ds) => ds.filter((x) => x.key !== d.key));
      toast.success(`${d.kind} eklendi: ${data.name}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const patchContact = async (c, payload) => {
    try {
      const { data } = await api.patch(`${base}/contacts/${c.id}`, payload);
      setContacts((cs) => cs.map((x) => (x.id === c.id
        ? { ...x, ...data, ...(payload.kind && payload.kind !== c.kind ? { project_ids: [], project_count: 0 } : {}) }
        : x)));
      toast.success("Kaydedildi");
      if (payload.zone && payload.zone !== (c.zone || "Green Zone")) {
        try {
          const r = await api.get(`${base}/contacts`);
          setContacts(r.data || []);
        } catch {}
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi? Projelerdeki atamaları da kaldırılır.`)) return;
    try {
      await api.delete(`${base}/contacts/${c.id}`);
      setContacts((cs) => cs.filter((x) => x.id !== c.id));
      toast.success("Silindi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openAssign = async (c) => {
    if (!projects) {
      try {
        const { data } = await api.get(projectsEndpoint);
        setProjects(data || []);
      } catch {
        return toast.error("Projeler yüklenemedi");
      }
    }
    setAssignFor(c);
  };

  const exportReport = () => {
    if (contacts.length === 0) return toast.error("Aktarılacak kayıt yok");
    const data = contacts.map((c) => ({
      "Tip": c.kind,
      "Bölge": c.zone || "Green Zone",
      "Ad Soyad": c.name,
      "Telefon": c.phone || "",
      "E-posta": c.email || "",
      "Proje Sayısı": c.project_count || 0,
      "Toplam m²": c.total_m2 || 0,
      "Projeler": (c.projects || []).map((p) => p.name).join(", "),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 6 }, { wch: 12 }, { wch: 24 }, { wch: 18 }, { wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 70 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SEM-CM Raporu");
    XLSX.writeFile(wb, "sem_cm_raporu.xlsx");
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`${base}/contacts/import-template`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sem_cm_sablon.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Şablon indirilemedi");
    }
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`${base}/contacts/import`, fd);
      const r = await api.get(`${base}/contacts`);
      setContacts(r.data || []);
      if (data.errors?.length) {
        toast.warning(`${data.created} kayıt eklendi · ${data.errors.length} satır hatalı (örn. satır ${data.errors[0].row}: ${data.errors[0].error})`);
      } else {
        toast.success(`${data.created} kayıt içe aktarıldı`);
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid={`${idp}-card`}>
      <div className="px-6 py-5 border-b border-slate-200 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
            <Users size={18} className="text-slate-600" /> SEM / CM Kişileri
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Satır ekleyerek SEM ve CM kişilerini kaydedin; "Proje Ata" ile birden fazla projeye atayın. Hücrelere tıklayıp düzenleyebilirsiniz.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={exportReport}
            data-testid={`${idp}-report-btn`}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-emerald-300 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors duration-150"
          >
            <FileSpreadsheet size={13} /> Rapor (Excel)
          </button>
          <button
            type="button"
            onClick={downloadTemplate}
            data-testid={`${idp}-template-btn`}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 text-xs font-semibold text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors duration-150"
          >
            <Download size={13} /> Örnek Dosya
          </button>
          <label
            data-testid={`${idp}-import-btn`}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 text-xs font-semibold text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors duration-150 cursor-pointer"
          >
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={importFile} data-testid={`${idp}-import-input`} />
            <Upload size={13} /> İçe Aktar
          </label>
          <Button onClick={addDraft} data-testid={`${idp}-add-row`} className="rounded-none h-9 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
            <Plus size={14} /> Satır Ekle
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
              <th className="px-3 py-3 font-medium">#</th>
              <th className="px-2 py-3 font-medium">Tip</th>
              <th className="px-2 py-3 font-medium">Bölge</th>
              <th className="px-2 py-3 font-medium">Ad Soyad</th>
              <th className="px-2 py-3 font-medium">Telefon</th>
              <th className="px-2 py-3 font-medium">E-posta</th>
              <th className="px-4 py-3 font-medium">Projeler</th>
              <th className="px-4 py-3 font-medium text-right whitespace-nowrap">Toplam m²</th>
              <th className="px-4 py-3 font-medium text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 && drafts.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-slate-400" data-testid={`${idp}-empty`}>
                  {!loaded ? "Yükleniyor..." : 'Henüz kayıt yok. "Satır Ekle" ile SEM veya CM ekleyin.'}
                </td>
              </tr>
            )}
            {contacts.map((c, i) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150" data-testid={`${idp}-row-${c.id}`}>
                <td className="px-3 py-2 text-slate-400 tabular-nums text-xs">{i + 1}</td>
                <td className="px-2 py-2">
                  <Select value={c.kind} onValueChange={(v) => v !== c.kind && patchContact(c, { kind: v })}>
                    <SelectTrigger data-testid={`${idp}-kind-${c.id}`} className={`rounded-none h-8 w-[90px] text-xs font-semibold ${KIND_CLS[c.kind] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      {kindOptsFor(c.zone || "Green Zone").map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-2">
                  <Select value={c.zone || "Green Zone"} onValueChange={(v) => v !== (c.zone || "Green Zone") && patchContact(c, { zone: v })}>
                    <SelectTrigger data-testid={`${idp}-zone-${c.id}`} className={`rounded-none h-8 w-[120px] text-xs font-semibold ${ZONE_CLS[c.zone || "Green Zone"] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      {zoneOptsFor(c.kind).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-2 min-w-[180px]">
                  <CellInput value={c.name} onSave={(v) => v && patchContact(c, { name: v })} placeholder="Ad Soyad" testid={`${idp}-name-${c.id}`} />
                </td>
                <td className="px-2 py-2 min-w-[150px]">
                  <CellInput value={c.phone} onSave={(v) => patchContact(c, { phone: v })} placeholder="+90 5xx xxx xx xx" testid={`${idp}-phone-${c.id}`} />
                </td>
                <td className="px-2 py-2 min-w-[180px]">
                  <CellInput value={c.email} onSave={(v) => patchContact(c, { email: v })} placeholder="kisi@firma.com" testid={`${idp}-email-${c.id}`} />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1.5">
                    {c.project_count > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid={`${idp}-projects-pop-${c.id}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors duration-150"
                            title="Atanan projeleri gör"
                          >
                            {c.project_count} proje
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="bg-white border-slate-200 rounded-none w-80 p-0" data-testid={`${idp}-projects-list-${c.id}`}>
                          <div className="px-3 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                            {c.name} — atanan projeler
                          </div>
                          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                            {(c.projects || []).map((p) => (
                              <div key={p.id} className="px-3 py-2">
                                <div className="text-xs font-semibold text-slate-800 truncate">{p.name}</div>
                                <div className="text-[10px] text-slate-400">{p.zone}{p.stand_no ? ` · Stand ${p.stand_no}` : ""}</div>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    <button
                      type="button"
                      onClick={() => openAssign(c)}
                      data-testid={`${idp}-assign-btn-${c.id}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 border border-slate-200 text-xs font-semibold text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors duration-150"
                    >
                      <FolderOpen size={13} /> Proje Ata
                    </button>
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-sm text-slate-700 whitespace-nowrap" data-testid={`${idp}-total-m2-${c.id}`}>
                  {c.project_count > 0 ? `${(c.total_m2 || 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} m²` : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => removeContact(c)}
                    data-testid={`${idp}-delete-${c.id}`}
                    title="Sil"
                    className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors duration-150"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {drafts.map((d, i) => (
              <tr key={d.key} className="border-b border-slate-100 last:border-0 bg-amber-50/40" data-testid={`${idp}-draft-row-${i}`}>
                <td className="px-3 py-2 text-slate-300 text-xs">—</td>
                <td className="px-2 py-2">
                  <Select value={d.kind} onValueChange={(v) => setDraft(d.key, { ...d, kind: v, ...(v === "SEM" && SEM_DISABLED_ZONES.includes(d.zone) ? { zone: "Green Zone" } : {}) })}>
                    <SelectTrigger data-testid={`${idp}-draft-kind-${i}`} className={`rounded-none h-8 w-[90px] text-xs font-semibold ${KIND_CLS[d.kind]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      <SelectItem value="SEM">SEM</SelectItem>
                      <SelectItem value="CM">CM</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-2">
                  <Select value={d.zone} onValueChange={(v) => setDraft(d.key, { ...d, zone: v })}>
                    <SelectTrigger data-testid={`${idp}-draft-zone-${i}`} className={`rounded-none h-8 w-[120px] text-xs font-semibold ${ZONE_CLS[d.zone] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      {zoneOptsFor(d.kind).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-2">
                  <Input value={d.name} onChange={(e) => setDraft(d.key, { ...d, name: e.target.value })} placeholder="Ad Soyad" data-testid={`${idp}-draft-name-${i}`} className="rounded-none h-8 text-sm bg-white border-slate-200 placeholder:text-slate-300" />
                </td>
                <td className="px-2 py-2">
                  <Input value={d.phone} onChange={(e) => setDraft(d.key, { ...d, phone: e.target.value })} placeholder="+90 5xx xxx xx xx" data-testid={`${idp}-draft-phone-${i}`} className="rounded-none h-8 text-sm bg-white border-slate-200 placeholder:text-slate-300" />
                </td>
                <td className="px-2 py-2">
                  <Input value={d.email} onChange={(e) => setDraft(d.key, { ...d, email: e.target.value })} placeholder="kisi@firma.com" data-testid={`${idp}-draft-email-${i}`} className="rounded-none h-8 text-sm bg-white border-slate-200 placeholder:text-slate-300" />
                </td>
                <td className="px-4 py-2 text-slate-300 text-xs">—</td>
                <td className="px-4 py-2 text-right text-slate-300 text-xs">—</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => saveDraft(d)}
                      data-testid={`${idp}-draft-save-${i}`}
                      className="h-8 px-3 inline-flex items-center gap-1 bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors duration-150"
                    >
                      <Check size={12} /> Kaydet
                    </button>
                    <button
                      type="button"
                      onClick={() => setDrafts((ds) => ds.filter((x) => x.key !== d.key))}
                      data-testid={`${idp}-draft-cancel-${i}`}
                      className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-600 transition-colors duration-150"
                    >
                      <X size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {assignFor && projects && (
        <AssignProjectsDialog
          contact={assignFor}
          projects={projects}
          base={base}
          idp={idp}
          onClose={() => setAssignFor(null)}
          onSaved={async (ids) => {
            setContacts((cs) => cs.map((x) => (x.id === assignFor.id ? { ...x, project_ids: ids, project_count: ids.length } : x)));
            setAssignFor(null);
            toast.success(`${assignFor.name} → ${ids.length} proje atandı`);
            try {
              const r = await api.get(`${base}/contacts`);
              setContacts(r.data || []);
            } catch {}
          }}
        />
      )}
    </div>
  );
};

export default function AssignerSettingsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="assigner-settings-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Settings size={10} /> Atayıcı Ayarlar</div>
          </div>
          <button
            onClick={() => navigate("/atayici")}
            data-testid="assigner-settings-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150"
          >
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Ayarlar</h1>
          <p className="text-sm text-slate-500 mt-1">SEM ve CM kişilerini yönetin, bölgenizdeki projelere atayın.</p>
        </div>
        <ContactsManager base="/assigner" projectsEndpoint="/assigner/projects" idp="assigner-contacts" />
      </main>
    </div>
  );
}
